/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { KeyValue } from '@angular/common';
import { WizardDTO } from '@api/models/';
import {
  ThemeCode,
  ErrorDisplay,
  HelpLinks,
  ThemeViews,
  AnalyticsEventName,
  QualityRangeModel,
  OtpContactChannel
} from '@core/models';
import { RouteCodes } from '@core/config/route-codes';
import { QuotationDataModel } from '../quotation';
import { EditableParam } from '../quotation-calculate';
import { StartDestination, NextDestination } from '../wizard';
import { HANDLER, METHOD } from './../recognition';

export interface AppConfigModel {
  /**
   * configurazione per la grafica
   */
  theme: ThemeConfigModel;

  /**
   * configurazioni generiche
   */
  generic: GenericConfigModel;

  /**
   * configurazioni di sviluppo
   */
  dev: DevConfigModel;

  /**
   * configurazione per gestione security
   */
  security: SecurityConfigModel;

  /**
   * configurazione position
   */
  position: PositionConfigModel;

  /**
   * configurazione del wizard
   */
  wizard: WizardConfigModel;

  /**
   * configurazioni dei survey
   */
  survey: SurveyConfigModel;

  /**
   * configurazione dei form
   */
  form: FormConfigModel;

  /**
   * configurazione upload file
   */
  upload: UploadConfigModel;

  /**
   * configurazione modulistica
   */
  publisher: PublisherConfigModel;

  /**
   * configurazione labels
   */
  labels: LabelsConfigModel;

  /**
   * configurazione riconoscimento
   */
  recognition: RecognitionConfigModel;

  /**
   * firma digitale
   */
  digitalSignature: DigitalSignatureConfigModel;

  /**
   * impegni
   */
  obligations: ObligationsConfigModel;

  /**
   * visualizzazione preventivo
   */
  quotation: QuotationConfigModel;

  /**
   * richiesta documenti
   */
  documentsRequest: DocumentsRequestConfigModel;

  /**
   * calcolo preventivo
   */
  quotationCalculate: QuotationCalculateConfigModel;

  /**
   * dashboard
   */
  dashboard: DashboardConfigModel;

  /**
   * funzioni di analytics
   */
  analytics: AnalyticsConfigModel;

  /**
   * ricerca atc
   */
  atc: AtcConfigModel;
}

export interface ThemeConfigModel {
  /**
   * flag per determinare se il logo sull'header è visibile
   */
  logoOnHeader?: boolean;

  /**
   * * testo primario del footer
   */
  footerPrimaryText?: string;

  /**
   * testo secondario del footer
   */
  footerSecondaryText?: string;

  /**
   * url del logo principale
   */
  logoUrl?: string;

  /**
   * stabilisce le view da visualizzare
   */
  activeViews?: [ThemeViews, boolean][];

  /**
   * modalità di visualizzazione del tooltip
   */
  tooltipInterface?: 'popover' | 'modal';

  /**
   * modalità di visualizzazione della conferma
   */
  confirmationInterface?: 'alert' | 'modal';

  /**
   * alias per le immagini
   */
  srcAlias?: { [key: string]: string };
}

export interface GenericConfigModel {
  /**
   * effettua redirect verso pagina di errore quando le chiamate vanno in 401
   */
  redirectUnauthorized?: boolean;

  /**
   * codice errore per cui è possibile impostare un redirect
   */
  errorsWithRedirect?: { [errorCode: string]: RouteCodes };

  /**
   * codici errore per cui non è più possibile per l'utente operare
   */
  blockingErrorCodes?: string[];

  /**
   * codici errore per cui è possibile continuare
   */
  warningErrorCodes?: string[];

  /**
   * definisce come gestire i codici di errori non previsti
   */
  unhandledErrorCodeAs?: string;

  /**
   * icone per i codici di errore
   */
  errorIcons?: { [code: string]: string };

  /**
   * modalità di visualizzazione degli errori
   */
  errorDisplay?: ErrorDisplay;

  /**
   * apparenza buttone retry su modal di errore (defualt: outline)
   */
  retryBtnAppearance?: 'solid' | 'outline';

  /**
   * apparenza buttone retry su modal di errore (defualt: solid)
   */
  assistanceBtnAppearance?: 'solid' | 'outline';

  /**
   * numero massimo di tentavi consentiti a seguito di un errore di un'api
   */
  maxAttempsAfterError?: number;

  /**
   * link di supporto
   */
  helpLinks?: [HelpLinks, string][];

  /**
   * codici paese accettati per l'inserimento dell'iban (inserire in maiusocolo; es "IT")
   */
  allowedIbanCountries?: string[];

  /**
   * se true invia i log tramite api dedicata
   */
  sendLogs?: boolean;

  /**
   * notifica l'utente se la connessione risulta essere assente
   */
  notifyConnectionLoss?: boolean;

  /**
   * notifica all'utente quando diventa inattivo
   */
  notifyIdle?: boolean;

  /**
   * tempo (in secondi) oltre il quale l'utente è considerato inattivo
   */
  idleTimeout?: number;

  /**
   * tempo (in secondi) in cui l'utente viene avvisato dello stato
   * di inattività e può dichiarare di essere tornato attivo.
   * il tempo effettivo dopo il quale l'utente viene disconnesso è pari
   * a idleTimeout + idleWarningTimeout
   */
  idleWarningTimeout?: number;

  /**
   * tipi di canale su cui è possibile applicare una verifica tramite api dedicata
   * prima che siano utilizzati per la funzione di otp
   */
  contactsNeedChecking?: OtpContactChannel[];

  /**
   * se attivo applica l'adattamento del timezone alle date gestite (form)
   */
  adaptTimezone?: boolean;
}

export interface DevConfigModel {
  operationCodes?: KeyValue<string, string>[];
}

export interface SecurityConfigModel {
  /**
   * nome dell'header http su cui viene applicato il request token
   */
  authHeaderName?: string;

  /**
   * determina se utilizzare l'header per inviare il request token oppure utilizzare il cookie
   */
  authStrategy?: 'header' | 'cookie';

  /**
   * nome della chiave con cui viene salvato il request token su storage
   */
  authStorageKeyName?: string;

  /**
   * elenco di url su cui non deve essere applicato l'header di autenticazione
   */
  urlOutOfAuth?: string[];

  /**
   * l'autenticazione richiede la presenza del query param "code" nell'url di ingresso
   * alla webapp. Il valore viene inviato all'authenticate attraverso la proprietà "uuid"
   */
  authCodeRequired?: boolean;

  /**
   * se true stabilisce in base alla presenza del query parma "code" se eseguire
   * una login con auth code o senza
   */
  dynamicAuthCodeEngage?: boolean;

  /**
   * se true non passa il sender nel body di authenticate
   */
  hideSender?: boolean;

  /**
   * attiva le notifiche push
   */
  pushNotificationActive?: boolean;

  /**
   * abilita autenticazione con sblocco biometrico
   */
  biometricAuthEnabled?: boolean;
}

export interface PositionConfigModel {
  /**
   * impedisce la navigazione delle pratica che risultano ritirate
   */
  preventNavOfRetreatPosition?: boolean;

  /**
   * impedisce la navigazione delle pratica che risultano concluse
   */
  preventNavOfConcludedPosition?: boolean;

  /**
   * lista di combinazioni fase/stato per cui la position
   * deve essere considerata conclusa;
   * scrivere i valori nel formato fase|status (es: ["300|RIT", "400|KO"])
   */
  considerPositionConcludedWhen?: string[];

  /**
   * lista di canali considerati speciali
   */
  specialChannels?: string[];

  /**
   * se attivo, la chiamata di release position viene eseguita in polling
   */
  usePollingOnRelease?: boolean;
}

export interface WizardConfigModel {
  /**
   * consente di ripetere il wizard una volta concluso
   */
  allowRestart?: boolean;

  /**
   * definisce da che pagina partire all'ingresso del wizard
   * - welcome: pagina di benvenuto con messaggio
   * - summary: pagina di riepilogo del wizard
   * - first-open-step: primo step da completare
   */
  startPage?: StartDestination;

  /**
   * definisce la pagina successiva al completamento di un step
   * - summary: pagina di riepilogo con gli step completati e quelli da completare;
   * - first-open-step: primo step da completare
   */
  nextPage?: NextDestination;

  /**
   * abilita la navigazione libera del wizard dalla schermata di riepilogo
   */
  freeNavigation?: boolean;

  /**
   * abilita il pulsante per visualizzare la finestra dello stato del processo
   */
  allowStateMenu?: boolean;

  /**
   * modalità con cui viene gestita la chiusura dei task;
   * - wizard: tramite api /positions/{positionId}/wizards/{wizardCode}/steps/{stepCode}/next
   * - workflow: tramite api /workflow/tasks/close/{positionId}
   */
  closingMode?: 'wizard' | 'workflow';

  /**
   * configurazione per wizard "finti"
   */
  dummyWizards?: { [code: string]: WizardDTO };

  /**
   * disabilita il back dallo step del wizard
   */
  avoidBack?: boolean;

  /**
   * è possibile associare ad un codice wizard un tema
   */
  themes?: { [code: string]: ThemeCode };

  /**
   * definisce lo stile della progress bar all'interno degli step:
   * relative: mostra l'avanzamento rispetto ai sotto step
   * absolute: mostra l'avanzamento rispetto al wizard
   */
  progressBar?: 'relative' | 'absolute';

  /**
   * è possibile raggruppare degli step in base al loro gruppo in modo
   * che lo stepper mostri un segmento per ogni step che fa parte del gruppo
   */
  progressBarGroups?: string[];

  /**
   * per gli step contenuti in questa lista sarà visualizzata una modale
   * con il messaggio corrispondente quando lo step viene completato, prima di passare
   * a quello successivo
   */
  stepsWithCloseMessage?: string[];

  /**
   * lista di stepcode dove è possibile ritirare la pratica
   * [0] => codice step
   * [1] => reason (facoltativa, se non indicata viene usato lo step code)
   */
  stopAllowedOn?: [string, string?][];

  /**
   * step code su cui è necessario controllare la ripresa pratica
   */
  checkResumeOn?: string[];

  /**
   * quando si esegue next su uno degli step indicati,
   * viene fatto poi reload della position
   */
  reloadPositionOn?: string[];

  /**
   * se true, viene aggiunto il path param valorizzato con
   * il codice dello step alla rotta che lo gestisce (default: false)
   */
  appendStepPathParam?: boolean;
}

export interface SurveyConfigModel {
  /**
   * tempo di debounce sul check sui campi dei form dinamici
   */
  debounceTime?: number;

  /**
   * visualizza i messaggi di warning al continua
   */
  alertWhenContinue?: boolean;

  /**
   * flag per utilizzare lo step code come page code
   */
  stepCodeAsCode?: boolean;

  /**
   * se true non viene applicata la divisione in colonne dei campi
   */
  fixedCol?: boolean;

  /**
   * flag per nascondere simbolo '*' nella label quando il campo è obbligatorio
   */
  hideMandatorySign?: boolean;

  /**
   * se true, la visualizzazione del documento non è obbligatoria
   */
  displayDocOptional?: boolean;

  /**
   * tipologia di visualizzazione select
   */
  selectInterface?: 'action-sheet' | 'popover';

  /**
   * tipologia di visualizzazione della search
   */
  searchInterface?: 'modal' | 'select';

  /**
   * flag per impedire l'aggiunta del query param referenceId sulla get
   */
  avoidReferenceId?: boolean;
}

export interface FormConfigModel {
  /**
   * tempo di debounce sul check sui campi dei form dinamici
   */
  debounceTime?: number;

  /**
   * visualizza i messaggi di warning al continua
   */
  alertWhenContinue?: boolean;

  /**
   * flag per utilizzare lo step code come page code
   */
  stepCodeAsCode?: boolean;

  /**
   * se true non viene applicata la divisione in colonne dei campi
   */
  fixedCol?: boolean;

  /**
   * azzera i campi figli quando il parent cambia valore
   */
  cleanChildren?: boolean;

  /**
   * imposta in automatico il valore del campo se le options caricate sono solo una
   */
  prefillWhenSingleOption?: boolean;

  /**
   * flag per nascondere simbolo '*' nella label quando il campo è obbligatorio
   */
  hideMandatorySign?: boolean;

  /**
   * se true, la visualizzazione del documento non è obbligatoria
   */
  displayDocOptional?: boolean;

  /**
   * determina il formato con cui le date vengono inviate a BE (millisecondi o iso)
   */
  dateFormat?: 'millisec' | 'iso';

  /**
   * numero di decimali da mostrare negli importi
   */
  amountDigits?: number;

  /**
   * tipologia di visualizzazione select
   */
  selectInterface?: 'action-sheet' | 'popover';

  /**
   * tipologia di visualizzazione della search
   */
  searchInterface?: 'modal' | 'select';

  /**
   * tipologia di visualizzazione della search
   */
  calendarInterface?: 'action-sheet' | 'popover';
}

export interface UploadConfigModel {
  /**
   * nome dell'header che definisce il file name nell'api di download
   */
  fileNameHeader?: string;

  /**
   * se attivo utilizza il file name estratto dall'header per recuperare la label
   */
  translateFileName?: boolean;

  /**
   * numero massimo di tentativi consentiti per l'upload di un documento a seguito di un errore
   */
  maxAllowedUploadAttempts?: number;

  /**
   * ordine con cui vengono richiesti all'utente i documenti da caricare;
   * va specificato il document code oppure il category code; se il documento ha entrambi
   * viene considerato il document code
   */
  uploadSorting?: string[];

  /**
   * ordine con cui vengono richiesti all'utente i documenti da validare;
   * va specificato il document code oppure il category code; se il documento ha entrambi
   * viene considerato il document code
   */
  checkSorting?: string[];

  /**
   * pagine richieste per gli upload dei documenti
   */
  documentPages?: { [docCode: string]: string };

  /**
   * codice documenti per cui è previsti un'immagine specifica
   */
  documentPictures?: string[];

  /**
   * modalità di acquisione dei documenti
   */
  acquisitionMode?: 'base' | 'mitek';

  /**
   * chiave di licenza mitek
   */
  mitekLicense?: string;

  /**
   * numero massimo di pagina che si possono aggiungere
   */
  additionalPages?: { [docCode: string]: number };

  /**
   * se true non mostra la schermata che indica qual'è la prossima pagina da caricare
   */
  avoidWhichPage?: boolean;

  /**
   * se true non mostra la schermata di istruzioni per effettuare la foto
   */
  avoidInstructions?: boolean;

  /**
   * attiva visualizzazione a griglia nella schermata di controllo file aggiunti
   */
  gridOnPreview?: boolean;

  /**
   * abilita lo switch to mobile
   */
  allowHandover?: boolean;

  /**
   * attiva la compressione delle immagini acquisite tramite fotocamera smartphone.
   * Se non specificato, il file viene ridotto alla dimensione di default di 1MB (circa)
   */
  compressPicture?: boolean;

  /**
   * specifica la dimensione massima delle immagini acquisite con smarthpone;
   * deve essere attivo anche il driver compressPicture
   */
  maxPictureSize?: number;

  /**
   * è possibile indicare i range di compressione in base alle dimensioni delle immagini
   */
  compressQualities?: QualityRangeModel[];

  /**
   * esegue la scansione dei documenti caricati
   */
  runScan?: boolean;

  /**
   * se attivo, il formato jpeg viene inviato come jpg
   */
  forceJpg?: boolean;
}

export interface PublisherConfigModel {
  /**
   * se true non viene mostrata il nome dell'estensione nei box per scaricare i documenti
   */
  hideExtension?: boolean;

  /**
   * se attivo, effettua direttamente il download del documento senza mostrare la modale di anteprima
   */
  downloadDirectly?: boolean;
}

export interface LabelsConfigModel {
  /**
   * url per caricare le label fuori da autenticazione
   */
  openLabelsUrl?: string;

  /**
   * è possibile filtrare le label caricate;
   * nelle key delle label verrà ricercato il pezzo contenuto tra []:
   * se non è presente la label verrà considerata,
   * se è presente la label verrà considerata solo se il valore corrisponde a quanto impostato qui
   */
  filter?: KeyValue<string, string>;
}

export interface RecognitionConfigModel {
  /**
   * opzioni per il metodo di riconoscimento SPID
   */
  spidOptions?: {
    /**
     * servizio esterno che gestisce il riconoscimento
     */
    handler: HANDLER;

    /**
     * specifica l'url della risorsa da richiamare per ottenere la lista di provider
     */
    providerResourceUrl?: string;

    /**
     * visualizza la pagina di scelta del provider prima di avviare il riconoscimento
     */
    providersChoice: boolean;
  };

  /**
   * opzioni per il metodo di riconoscimento videoselfie
   */
  videoselfieOptions?: {
    /**
     * servizio esterno che gestisce il riconoscimento
     */
    handler: HANDLER;
  };

  /**
   * metodo di riconoscimento consigliato
   */
  recommendedMethod?: METHOD;

  /**
   * numero massimo di tentativi
   */
  maxAttemps?: number;
}

export interface DigitalSignatureConfigModel {
  /**
   * è possibile specificare lo stato che deve avere la pratica
   * per considerare la verifica della firma come completa;
   * utilizzare formato <fase>|<stato> (es: "100|RIT")
   */
  positionMustBe?: [string, string][];

  /**
   * codice di errore che identifica la scadenza delle sessione di firma sulla start
   */
  signatureExpireError?: string;

  /**
   * è possibile evitare che venga fatto next sul wizard dopo la verify
   * in quei processi in cui si sa che il next viene fatto in background dal BE
   */
  avoidNextAfterVerify?: boolean;

  /**
   * determina la frequenza di aggiornamento del polling in secondi (default 5 sec)
   */
  pollingFrequency?: number;

  /**
   * determina il tempo massimo di attesa in secondi (default infinito)
   */
  pollingMaxTime?: number;
}

export interface ObligationsConfigModel {
  /**
   * determina se è richiesta la conferma esplicita dell'estinzione degli impegni
   */
  explicitConfirmation?: boolean;

  /**
   * flag che determina l'aggiunta di impegni
   */
  additionAllowed?: boolean;
}

export interface QuotationConfigModel {
  /**
   * dati primari da visualizzare nella card del preventivo
   */
  primaryData?: QuotationDataModel[];

  /**
   * Parametri che è possibile modificare nella conferma preventivo.
   * Implicitamente stabilisce se è possibile modificare il preventivo
   */
  editableParams?: EditableParam[];

  /**
   * abilita il rifiuta preventivo
   */
  rejectAllowed?: boolean;

  /**
   * tipo di visualizzazione
   */
  view?: 'step-by-step' | 'inline';

  /**
   * codici modulo da visionare per il preventivo
   */
  moduleCodes?: string[];

  /**
   * codici modulo la cui visione è obbligatoria
   */
  moduleCodesToView?: string[];

  /**
   * codici modulo la cui presa visione è obbligatoria
   */
  moduleCodesToMark?: string[];

  /**
   * se true, non viene controllata la presenza in checklist del documento
   * per considerare il preventivo accettato
   */
  skipDocumentCheck?: boolean;

  /**
   * se true, abilita subito il pulsante conferma ma evidenzia le sezioni
   * che eventualmente non sono state compilate (come lo scarico del SECCI)
   */
  highlightsWhatMissing?: boolean;
}

export interface DocumentsRequestConfigModel {
  /**
   * codici per identificare le operazioni di richiesta documentazione aggiuntiva
   */
  additionalRequestOperationCodes?: string[];

  /**
   * definisce un prefisso da aggiungere alla form page di verifica dei documenti
   */
  checkFormPrefix?: string;

  /**
   * permette di specificare quale attributo devono avere i documenti da validare;
   * se non specificato viene usato il campo flagToValidate
   */
  identifyToValidateWithAttribute?: string;

  /**
   * @legacy
   * le app bib non valorizzano la prop mandatoryFor con lo step
   * del wizard, per cui è necessario ignorarlo come accadeva nel primo sviluppo
   */
  ignoreMandatoryFor?: boolean;
}

export interface QuotationCalculateConfigModel {
  /**
   * lista delle sezioni da mostrare prima del calcolo
   */
  preparationSections?: EditableParam[];

  /**
   * sezioni che è possibile modificare dopo il calcolo
   */
  editableSections?: EditableParam[];

  /**
   * regola la visualizzazione dei dettagli:
   * true: vengono mostrati direttamente sotto il preventivo
   * false: compare un bottone per aprire pannello con i dettagli
   */
  detailsInline?: boolean;
}

export interface DashboardConfigModel {}

export interface AnalyticsConfigModel {
  /**
   * attiva le funzionalità di analytics
   */
  enabled?: boolean;

  /**
   * lista di eventi spenti
   */
  eventsOff?: AnalyticsEventName[];
}

export interface AtcConfigModel {
  /**
   * abilita la ricerca avanzata
   */
  advancedSearch?: boolean;
}
